// Phase 13 — SE Sale Return with sku_lines editor
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Camera, CheckCircle2, Trash2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { mutate as offlineMutate } from "@/offline/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useDistributors, useRetailers, useSkus, compressAndUploadPhoto, idempKey, useGps, StatusPill } from "./_helpers";

const REASONS = [
  { code: "damaged", label: "Damaged" }, { code: "expired", label: "Expired" },
  { code: "wrong_product", label: "Wrong Product" }, { code: "near_expiry", label: "Near Expiry" },
  { code: "other", label: "Other (specify)" },
];

function LineEditor({ line, onChange, onRemove, skus }) {
  return (
    <div className="border rounded-md p-2 space-y-2 bg-neutral-50">
      <div className="flex gap-2 items-center">
        <div className="flex-1">
          <Label className="text-[11px]">SKU</Label>
          <Select value={line.sku_id || "__none__"} onValueChange={(v) => onChange({ sku_id: v === "__none__" ? "" : v })}>
            <SelectTrigger className="h-8"><SelectValue placeholder="Pick SKU" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— none —</SelectItem>
              {skus.map(s => <SelectItem key={s.id} value={s.id}>{s.sku_code} · {s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-20">
          <Label className="text-[11px]">Qty</Label>
          <Input className="h-8" type="number" min="1" step="1" value={line.qty}
                 onChange={(e) => onChange({ qty: Number(e.target.value) })} />
        </div>
        <button onClick={onRemove} className="text-rose-500 self-end mb-1"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div>
        <Label className="text-[11px]">Reason</Label>
        <Select value={line.reason} onValueChange={(v) => onChange({ reason: v })}>
          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
          <SelectContent>{REASONS.map(r => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {line.reason === "other" && (
        <div>
          <Label className="text-[11px]">Please describe *</Label>
          <Input className="h-8" value={line.remarks || ""} onChange={(e) => onChange({ remarks: e.target.value })} />
        </div>
      )}
    </div>
  );
}

export default function SaleReturnNew() {
  const nav = useNavigate();
  const dists = useDistributors();
  const rets = useRetailers();
  const skus = useSkus();
  const gps = useGps();
  const [distributorId, setDistributorId] = useState("");
  const [retailerId, setRetailerId] = useState("");
  const [lines, setLines] = useState([{ sku_id: "", qty: 1, reason: "damaged", remarks: "" }]);
  const [photos, setPhotos] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  function updateLine(idx, patch) {
    setLines(ls => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));
  }
  function removeLine(idx) {
    setLines(ls => ls.filter((_, i) => i !== idx));
  }

  async function addPhoto(e) {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const ref = await compressAndUploadPhoto(f);
      setPhotos(p => [...p, ref]); toast.success("Photo added");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { e.target.value = ""; }
  }

  async function submit() {
    if (!retailerId) return toast.error("Choose a retailer");
    if (!distributorId) return toast.error("Choose a distributor");
    if (lines.length === 0) return toast.error("Add at least one line");
    for (const l of lines) {
      if (!l.sku_id) return toast.error("Pick SKU for every line");
      if (!l.qty || l.qty <= 0) return toast.error("Qty must be > 0");
      if (l.reason === "other" && !(l.remarks || "").trim())
        return toast.error("Remarks required when reason=Other");
    }
    setBusy(true);
    try {
      const res = await offlineMutate({
        method: "POST", path: "/sale-returns", entity_type: "sale_returns",
        body: {
          retailer_id: retailerId, distributor_id: distributorId,
          sku_lines: lines, photos, remarks: remarks || null, gps,
          client_ts: new Date().toISOString(),
          idempotency_key: idempKey("sr"),
          offline_captured: !navigator.onLine,
        },
      });
      if (res.immediate) toast.success("Return submitted");
      else toast.success("Queued — will sync when online");
      nav("/money/sale-returns");
    } catch (e) { toast.error(e?.message || "Submit failed"); }
    finally { setBusy(false); }
  }

  return (
    <div className="max-w-lg mx-auto p-4 space-y-4" data-testid="return-new-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <h1 className="font-semibold flex-1">Log a sale return</h1>
      </div>

      <div>
        <Label>Retailer *</Label>
        <Select value={retailerId || "__none__"} onValueChange={(v) => setRetailerId(v === "__none__" ? "" : v)}>
          <SelectTrigger data-testid="sr-retailer"><SelectValue placeholder="Choose retailer" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— none —</SelectItem>
            {rets.map(r => <SelectItem key={r.id} value={r.id}>{r.code} · {r.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Distributor *</Label>
        <Select value={distributorId || "__none__"} onValueChange={(v) => setDistributorId(v === "__none__" ? "" : v)}>
          <SelectTrigger data-testid="sr-distributor"><SelectValue placeholder="Choose distributor" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— none —</SelectItem>
            {dists.map(d => <SelectItem key={d.id} value={d.id}>{d.code} · {d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <div className="flex justify-between items-center">
          <Label>Return lines *</Label>
          <Button size="sm" variant="outline" data-testid="sr-add-line"
                  onClick={() => setLines(ls => [...ls, { sku_id: "", qty: 1, reason: "damaged", remarks: "" }])}>
            + Add line
          </Button>
        </div>
        <div className="space-y-2 mt-2">
          {lines.map((l, i) => (
            <LineEditor key={i} line={l} skus={skus}
                        onChange={(patch) => updateLine(i, patch)}
                        onRemove={() => removeLine(i)} />
          ))}
        </div>
      </div>

      <div>
        <Label>Photos ({photos.length})</Label>
        <div className="grid grid-cols-3 gap-2 mt-1">
          {photos.map((p, i) => (
            <div key={i} className="relative border rounded bg-emerald-50 border-emerald-500 aspect-square flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <button onClick={() => setPhotos(ps => ps.filter((_, x) => x !== i))} className="absolute -top-1 -right-1 bg-white border rounded-full p-0.5"><X className="w-3 h-3" /></button>
            </div>
          ))}
          <label htmlFor="sr-photo-input" className="border border-dashed rounded aspect-square flex items-center justify-center cursor-pointer text-neutral-500 hover:border-emerald-400">
            <Camera className="w-4 h-4" />
          </label>
          <input id="sr-photo-input" data-testid="sr-photo" type="file" accept="image/*" capture="environment" className="hidden" onChange={addPhoto} />
        </div>
      </div>

      <div>
        <Label>Remarks</Label>
        <Textarea data-testid="sr-remarks" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
      </div>

      <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={submit} disabled={busy} data-testid="sr-submit">
        <CheckCircle2 className="w-4 h-4 mr-2" /> {busy ? "Submitting…" : "Submit return"}
      </Button>
    </div>
  );
}


export function SaleReturnsList() {
  const nav = useNavigate();
  const [items, setItems] = useState([]); const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/sale-returns?page_size=100").then(r => setItems(r.data.items || [])).finally(() => setLoading(false));
  }, []);
  return (
    <div className="max-w-lg mx-auto p-4 space-y-4" data-testid="returns-list-page">
      <div className="flex items-center justify-between">
        <h1 className="font-semibold text-lg">Sale Returns</h1>
        <Button size="sm" data-testid="sr-new-btn" onClick={() => nav("/money/sale-returns/new")}
                className="bg-emerald-600 hover:bg-emerald-700">+ New return</Button>
      </div>
      {loading ? <div className="text-neutral-500 text-sm">Loading…</div>
       : items.length === 0 ? <div className="text-neutral-500 text-sm border border-dashed rounded-lg p-6 text-center">No returns yet</div>
       : items.map(r => (
         <div key={r.id} className="border rounded-lg p-3 bg-white" data-testid={`return-row-${r.id}`}>
           <div className="flex justify-between items-start">
             <div className="min-w-0">
               <div className="font-medium truncate">{r.retailer_name_snapshot}</div>
               <div className="text-xs text-neutral-500 truncate">{r.distributor_name_snapshot}</div>
               <div className="text-xs text-neutral-400 mt-1">{(r.sku_lines || []).length} lines · v{r.version}</div>
             </div>
             <div className="text-right">
               <div className="font-semibold">₹ {Number(r.amount || 0).toLocaleString("en-IN")}</div>
               <StatusPill status={r.status} />
             </div>
           </div>
         </div>
       ))
      }
    </div>
  );
}
